import { MouseEvent } from 'react'
import { useFormContext } from 'react-hook-form'
import { v4 as uuid } from 'uuid'
import { Button } from '@microservices/wiskey-react-components'
import { CircularProgress, Typography } from '@mui/material'

import { getFormData } from '@components/DisplayForm/helpers'
import { VariableValue } from '@components/Variables/types'

import { useExecuteCommandMutation, useFetchActionByIdQuery } from '@redux/api'
import { openFormDialog } from '@redux/reducers/dialogWindowManager.reducer'
import { addFormHistory } from '@redux/reducers/formHistory.reducer'

import { useAppDispatch } from '@hooks'
import { getCommandRequestData, getInitialPositionWindow, getValuesFromVariables } from '@helpers'
import { ACTION_CODE, ENTITY, FORM_TYPE } from '@constants'
import {
  DIALOG_WINDOW_TYPE,
  FormDataEnriched,
  FormElement,
  FormRow,
  GETVariableDTO,
  Row,
} from '@types'

import { EVENT_NAME, publish } from '../../events'

type ActionButtonType = {
  element: FormElement<FormDataEnriched>
  row: Row
  currentRowValues: Row
  fieldsFormConfig?: FormRow[]
  latestVariables: GETVariableDTO[]
  outerVariables: VariableValue[]
}

export const ActionButton = ({
  element,
  row,
  currentRowValues,
  fieldsFormConfig,
  outerVariables,
  latestVariables,
}: ActionButtonType) => {
  const { watch } = useFormContext()

  const dispatch = useAppDispatch()
  const { data: actionData, isLoading } = useFetchActionByIdQuery(element.data as number, {
    skip: !element.data,
  })

  const [executeCommand, { isLoading: isCommandLoading }] = useExecuteCommandMutation()

  const handleClick = async (event: MouseEvent<HTMLButtonElement>) => {
    if (!actionData) return

    const { command, actionCode, variable, jsValue } = actionData

    const srcObj = {
      rowData: row,
      currentRowValues,
      formVariables: latestVariables,
      outerVariables: getValuesFromVariables(outerVariables),
      formValues: watch(),
      enrichedObject: row, // Дублирование, нужно проверить используется ли где-то такой параметр
    }

    if (command) {
      if (actionCode === ACTION_CODE.EXECUTE_COMMAND && command) {
        try {
          const body = jsValue
            ? new Function('srcObj', jsValue)(srcObj)
            : getCommandRequestData(command.body ?? [], currentRowValues, undefined, undefined, {
                ...srcObj,
              })

          const hasFile = Object.values(body).some(value => value instanceof File)

          const params = getCommandRequestData(
            command.parameters ?? [],
            currentRowValues,
            undefined,
            undefined,
            {
              ...srcObj,
            }
          )

          await executeCommand({
            method: command.httpMethod,
            objectCode: command.objectCode,
            commandCode: command.name,
            isCommandResponse: Boolean(command.isCommandResponse),
            params,
            body: hasFile ? getFormData(body) : body,
          })
        } catch (err) {
          console.log(err)
        }

        return
      }

      if (actionCode === ACTION_CODE.CHANGE_VARIABLE_VALUE) {
        try {
          const body = jsValue
            ? new Function('srcObj', jsValue)(srcObj)
            : getCommandRequestData(command.body ?? [], currentRowValues, false, fieldsFormConfig, {
                ...srcObj,
              })

          const hasFile = Object.values(body).some(value => value instanceof File)

          const commandData = await executeCommand({
            method: command.httpMethod,
            objectCode: command.objectCode,
            commandCode: command.name,
            isCommandResponse: Boolean(command.isCommandResponse),
            params: getCommandRequestData(
              command.parameters ?? [],
              currentRowValues,
              undefined,
              undefined,
              {
                ...srcObj,
              }
            ),
            body: hasFile ? getFormData(body) : body,
          }).unwrap()

          const newVariableValue = {
            ...variable,
            entityId: element.data,
            value: commandData,
            updatedOn: new Date().toISOString(),
          }

          publish(EVENT_NAME.CHANGE_VARIABLE_VALUE, newVariableValue)
        } catch (err) {
          console.log(err)
        }

        return
      }
    }

    const id = uuid()
    const { objectFormCode, objectCode } = actionData
    const initialPosition = getInitialPositionWindow(event)

    dispatch(
      addFormHistory({
        id,
        form: {
          formCode: objectFormCode ?? '',
          objectCode: objectCode ?? '',
          event: FORM_TYPE.CREATE,
          objectId: null,
          title: '',
          id,
          isWindow: true,
        },
      })
    )

    dispatch(
      openFormDialog({
        id,
        parentDialogId: null,
        type: DIALOG_WINDOW_TYPE.FORM,
        title: null,
        meta: {
          objectCode: objectCode ?? '',
          variables: srcObj?.formVariables,
          formCode: objectFormCode ?? '',
          viewCode: '',
          path: objectFormCode ?? '',
          event: FORM_TYPE.CREATE,
          globalId: id,
          entityType: ENTITY.VIEW,
          isIndependentForm: true,
          outerData: {
            commands: actionData.redefinitionCommands ?? [],
            srcObj: row,
          },
        },
        initialPosition,
      })
    )
  }

  return (
    <Button
      disabled={!actionData || isLoading || isCommandLoading}
      variant='contained'
      sx={theme => ({
        position: 'relative',
        height: 25,
        cursor: 'pointer',
        ':disabled': {
          cursor: 'default',
        },
      })}
      onClick={handleClick}
    >
      <Typography
        variant='body2'
        sx={{
          whiteSpace: 'nowrap',
          textOverflow: 'ellipsis',
          overflow: 'hidden',
        }}
      >
        {element.title}
      </Typography>
      {isCommandLoading && (
        <CircularProgress size={14} sx={{ ml: 2, position: 'absolute', right: 8 }} />
      )}
    </Button>
  )
}
